
















import loadImage from 'blueimp-load-image';
import { Component, Model, Prop } from 'vue-property-decorator';

import Dropzone from '@/componentsV2/molecules/Dropzone/Dropzone.vue';
import PaperClip from '@/components/icons/PaperClip.vue';
import ImageUploadIcon from '@/components/icons/ImageUploadIcon.vue';
import CloseCircleIcon from '@/components/icons/CloseCircle.vue';
import { HTMLInputEvent } from '@/components/TextArea/TextAreaModel';
import InputBase from '@/componentsV2/molecules/Inputs/InputBase.vue';
import InputWrapper from '@/componentsV2/molecules/Inputs/InputWrapper.vue';
import ModalOptions from '@/components/Modal/ModalOptions';
import Loading from '@/components/icons/Loading.vue';
import CloseCircle from '@/components/icons/CloseCircle.vue';
import ImageSpecialCoinPreview from '@/components/organisms/Offers/ImageSpecialCoinPreview.vue';

import UploadImageService from '@/api/ImageUploadService';
import OffersService from '@/api/OffersService';

import eventBus from '@/utils/eventBus';
import { dataURItoBlob, getBase64FromUrl } from '@/utils/image';

type OfferImageUploadValue = {
  imageFile?: File | null;
  imageUrl: string;
};

const initialState = () => ({ imageFile: null, imageUrl: '' });

@Component({
  components: {
    InputWrapper,
    Dropzone,
    PaperClip,
    ImageUploadIcon,
    CloseCircleIcon,
    Loading,
    CloseCircle,
    ImageSpecialCoinPreview
  }
})
export default class OfferImageUpload extends InputBase {
  @Prop({ default: false }) isOptional?: boolean;
  @Prop({ default: true }) showHelpIcon?: boolean;
  @Prop({ default: false }) loading?: boolean;
  @Prop({ default: false }) uploadFailed?: boolean;
  @Prop({ default: true }) automaticUpload?: boolean;
  @Prop({ default: false }) disabled?: boolean;
  @Prop({ default: true }) automaticUpdateOffer?: boolean;
  @Prop() instanceId?: string;
  @Prop(String) offerId!: string;
  @Prop({ default: '212px' }) size?: string;

  @Model('input', {
    type: Object,
    default: () => initialState()
  })
  value!: OfferImageUploadValue;
  cropMinZoom = 1;
  showPreview = false;
  initialCropState: any = null;
  imageId: string = '';
  nameFile: string = '';
  originalFile: string = '';
  previousOriginalFile: string = '';
  boundaryOptions = this.getSize();
  viewportOptions = {
    ...this.boundaryOptions
  };
  options = {
    type: 'blob',
    size: 'original',
    circle: false,
    quality: 0.85,
    format: 'jpeg'
  };

  uploadImageService = new UploadImageService();
  offerService = new OffersService();

  get labelField() {
    return this.$t(`${this.tPrefix}.label`);
  }

  getSize() {
    const size = 352;

    return {
      width: size,
      height: size
    };
  }

  handleReset() {
    if (this.disabled) {
      return;
    }

    this.$emit('input', initialState());
  }

  onInput(event: HTMLInputEvent) {
    const files = event.target.files || [];

    this.handleDropzoneInput(Array.from(files));
  }

  get tPrefix() {
    return 'common.components.imageUploadBlock';
  }

  handleDropzoneInput(files: File[]) {
    const [file] = files;

    if (!file) return;

    this.$emit('selectedImage', file);

    const supportedFileTypes = ['jpeg', 'png', 'jpg', 'svg'];
    if (!supportedFileTypes.some(type => file.type.includes(type))) {
      this.$notification(
        String(this.$t(`${this.tPrefix}.warnFormatFile`)),
        'warn'
      );
      return;
    }

    const sizeInMB = file.size / 1024 / 1024;

    if (sizeInMB > 10) {
      this.$notification(
        String(this.$t(`${this.tPrefix}.warnMaximumFileSize`)),
        'warn'
      );
      return;
    }

    this.$showLoaderWithLocker();
    this.initialCropState = null;
    this.nameFile = file.name;

    loadImage(
      file,
      async (canvas: HTMLCanvasElement) => {
        const link = canvas.toDataURL('image/png', 1);
        const image = new Image();
        image.onload = () => {
          const minDimesion = Math.min(image.width, image.height);
          this.cropMinZoom = this.boundaryOptions.width / minDimesion;

          this.originalFile = link;
          this.enterEditMode();

          this.$hideLoader();
        };
        image.src = link;
      },
      { canvas: true, meta: true, orientation: true }
    );
  }

  async saveImage(actualFile: Blob) {
    this.previousOriginalFile = String(this.originalFile);

    this.$emit('start-upload');

    const formData = new FormData();
    formData.append('data', actualFile);

    const {
      status: uploadStatus,
      data: uploadData
    } = await this.uploadImageService.upload(formData);

    if (uploadStatus !== 201) {
      this.$emit('end-upload', { success: false });
      return;
    }

    let status = uploadStatus;

    if (this.automaticUpdateOffer) {
      const { status: updateStatus } = await this.offerService.updateOffer(
        this.offerId,
        {
          imageId: uploadData.id,
          setActive: !this.value.imageUrl
        }
      );

      status = updateStatus;
    }

    this.$emit('end-upload', {
      success: status === 201 || status === 200,
      offerId: this.offerId,
      image: {
        id: uploadData.id,
        url: uploadData.publicPath.large
      }
    });

    if (status === 200) {
      this.$emit('input', {
        imageFile: actualFile,
        imageUrl: uploadData.publicPath.large,
        imageName: this.nameFile
      });

      this.restetInputFile();
    }
  }

  saveEditMode({ img, cropState, original }: any) {
    eventBus.$emit('closeModal');

    this.initialCropState = cropState;

    if (!this.automaticUpload) {
      this.$emit('input', {
        imageUrl: img,
        imageName: this.nameFile
      });
      return;
    }
    const output = dataURItoBlob(img);

    this.showPreview = true;

    this.saveImage(output);
  }

  restetInputFile() {
    const inputFile = document.getElementById(
      this.instanceId || ''
    ) as HTMLInputElement;

    if (inputFile) {
      inputFile.value = '';
    }
  }

  onCancelEditMode() {
    this.restetInputFile();

    this.originalFile = String(this.previousOriginalFile);
    eventBus.$emit('closeModal');
  }

  get cropTitleLabel() {
    return 'Offer Image';
  }

  async enterEditMode() {
    const {
      initialCropState,
      boundaryOptions,
      viewportOptions,
      saveEditMode,
      onCancelEditMode,
      imageId,
      options,
      cropTitleLabel
    } = this;

    const src = this.originalFile
      ? this.originalFile
      : await getBase64FromUrl(this.value.imageUrl);

    this.originalFile = src;

    eventBus.$emit(
      'openSubModal',
      new ModalOptions(
        ``,
        ``,
        'none',
        true,
        ``,
        ``,
        ``,
        false,
        'cropModal',
        false,
        true,
        true,
        false,
        true,
        null,
        false
      ),
      {
        initialState: initialCropState,
        onSave: saveEditMode,
        onCancel: onCancelEditMode,
        boundaryOptions,
        viewportOptions,
        options,
        minZoom: String(this.cropMinZoom),
        scaleImage: true,
        src,
        title: cropTitleLabel,
        actualImage: null,
        imageId,
        roundedCropWrapper: true,
        returnTo: null,
        maxZoom: 0.99,
        initialZoom: 0,
        radius: '20px',
        borderColor: '#E4EBF2'
      }
    );
  }
}
